import {useMemo, useState} from "react";
import {StatisticsGroupWrapper} from "@ots-frontend/ots-data";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import {useDebouncedValue} from "@mantine/hooks";
import { StatisticsDataTableProps } from "./StatisticsDataTable";

export type UseStatisticsDataTableFilterProps = Omit<UseDataTableFilterProps<StatisticsGroupWrapper>, 'filterConfig'>
  & Pick<StatisticsDataTableProps, 'category' | 'method'>

export type UseStatisticsDataTableFilterResult = UseDataTableFilterResult<StatisticsGroupWrapper>

export function useStatisticsDataTableFilter(props: UseStatisticsDataTableFilterProps): UseStatisticsDataTableFilterResult {
  const {records, category, method} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const filterConfig: DataTableFilterConfig<StatisticsGroupWrapper> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: (record, query) => !record.name || !record.name.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    t
  ])

  const preFilteredRecords = useMemo(() => {
    return records?.filter((record) => record.getRecordCount(method, category) > 0)
  }, [records, method, category])

  const {filteredRecords} = useDataTableFilter({
    records: preFilteredRecords,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
